import { useState, useRef } from 'react'
import { Upload, FileText, X, Loader2, CheckCircle } from 'lucide-react'
import { getAuthToken } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import { API_BASE } from '@/services/api'

interface UploadedFile {
  file_id: string
  filename: string
  size: number
}

interface KBFileUploaderProps {
  onFilesUploaded: (files: UploadedFile[]) => void
  uploadedFiles: UploadedFile[]
  disabled?: boolean
}

export default function KBFileUploader({ onFilesUploaded, uploadedFiles, disabled }: KBFileUploaderProps) {
  const { tenantId } = useTenant()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const getHeaders = async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'X-Tenant-Id': tenantId || '',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  const uploadFiles = async (fileList: FileList) => {
    if (disabled) return
    setLoading(true)
    setError('')

    try {
      const newFiles: UploadedFile[] = []
      const headers = await getHeaders()

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i]

        // Validate extension
        const validExtensions = ['.pdf', '.md', '.txt', '.docx', '.json', '.csv']
        const hasValidExt = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))

        if (!hasValidExt) {
          setError(`File ${file.name} not supported. Only PDF, MD, TXT, DOCX, JSON, CSV allowed`)
          continue
        }

        const formData = new FormData()
        formData.append('file', file)

        const response = await fetch(`${API_BASE}/v1/knowledge-base/upload`, {
          method: 'POST',
          headers,
          body: formData,
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || `Upload failed: ${response.status}`)
        }

        const data = await response.json()
        if (data.success) {
          newFiles.push({
            file_id: data.file_id,
            filename: data.filename,
            size: data.size,
          })
        }
      }

      onFilesUploaded([...uploadedFiles, ...newFiles])
    } catch (err: any) {
      setError(err.message || 'Error uploading files')
    } finally {
      setLoading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    const files = e.target.files
    if (!files || files.length === 0) return
    await uploadFiles(files)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      await uploadFiles(files)
    }
  }

  const removeFile = (fileId: string) => {
    onFilesUploaded(uploadedFiles.filter(f => f.file_id !== fileId))
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Files</h3>

      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      <div
        onClick={() => {
          if (!disabled) {
            fileInputRef.current?.click()
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
          disabled
            ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-70'
            : ''
        } ${
          isDragging
            ? 'border-yellow-500 bg-yellow-50 scale-[1.02]'
            : 'border-gray-300 bg-gray-50 hover:border-yellow-500 hover:bg-yellow-50/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.md,.txt,.docx,.json,.csv"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
        <div className="flex flex-col items-center">
          {loading ? (
            <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mb-3" />
          ) : (
            <Upload className={`w-12 h-12 mb-3 ${isDragging ? 'text-yellow-500' : 'text-gray-400'}`} />
          )}
          <p className="font-medium text-gray-700">
            {loading
              ? 'Uploading...'
              : disabled
                ? 'Uploads disabled'
                : isDragging
                  ? 'Drop files here'
                  : 'Drag files or click to upload'
            }
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Formats: PDF, MD, TXT, DOCX, JSON, CSV
          </p>
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Uploaded Files ({uploadedFiles.length})
          </h4>
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <div
                key={file.file_id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white rounded-lg border border-gray-200">
                    <FileText className="w-4 h-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{file.filename}</p>
                    <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Uploaded
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeFile(file.file_id)
                    }}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
